import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import Card from '../../../components/ui/Card';
import { LoadingSpinner } from '../../../components/ui/LoadingSpinner';
import Pagination from '../../../components/ui/Pagination';
import NameWithEmailTooltip from '../../../components/ui/NameWithEmailTooltip';
import ErrorState from '../../../components/ui/ErrorState';
import { nominationReviewService, type NominationReviewResponse } from '../../../services';
import { useAuth } from '../../../hooks/useAuth';
import type { ApiNominationResponse, ApiNominator } from '../../../types';
import tuneIcon from '../../../assets/tune.svg';
import arrowDropDownIcon from '../../../assets/arrow_drop_down_filter.svg';
import SearchFilterBar from '../../../components/ui/SearchFilterBar';
import FiltersModal from '../../../components/ui/FiltersModal';
import NoActiveCycle from '../../../components/ui/NoActiveCycle';
import { useActiveCycle } from '../../../hooks/useActiveCycle';

const PageSizeOptions = [10, 20, 30, 50];

// Use the same interface as the API response
type Nomination = NominationReviewResponse & {
  nominationRelationshipId?: number;
  review_exists?: boolean;
  employeeEmail?: string;
};

const NominationReviewPageView: React.FC = () => {
  const navigate = useNavigate();
  const { user, isLoading: authLoading } = useAuth();
  const hasActiveCycle = useActiveCycle();
  const [nominations, setNominations] = useState<Nomination[]>([]);
  const [loading, setLoading] = useState(true);
  
  // Track if data has been fetched to prevent duplicate API calls
  const hasFetched = React.useRef(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    department: '',
    designation: '',
    nominationCapacity: '',
    reviewStatus: ''
  });
  const [tempFilters, setTempFilters] = useState({
    department: '',
    designation: '',
    nominationCapacity: '',
    reviewStatus: ''
  });
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);

  // Debug user state
  console.log('NominationReviewPageView - user state:', user);
  console.log('NominationReviewPageView - loading state:', loading);

  const fetchNominations = useCallback(async () => {
    // Don't fetch if auth is still loading or no user email
    if (authLoading || !user?.email) {
      return;
    }
    
    // Prevent duplicate API calls
    if (hasFetched.current) {
      return;
    }
    hasFetched.current = true;

    console.log('✅ All checks passed, making API call with email:', user.email);
    setLoading(true);
    
    try {
      const response = await nominationReviewService.getNominationReviews(user.email);
      console.log('✅ API Response received:', response);
      console.log('📊 Response data structure:', response.data);
      
      // Extract nominators array from the API response
      const apiData = response.data as unknown; 
      let nominators: ApiNominator[] = [];
      
      // Type guard to check if it's the new API structure
      const isNewApiStructure = (data: unknown): data is ApiNominationResponse => {
        return (
          typeof data === 'object' && 
          data !== null && 
          'nominators' in data && 
          Array.isArray((data as ApiNominationResponse).nominators)
        );
      };
      
      if (isNewApiStructure(apiData)) {
        nominators = apiData.nominators;
        console.log('📋 Found nominators array:', nominators);
      
      // Log review_exists values for debugging
      nominators.forEach((nominator: ApiNominator, index: number) => {
        console.log(`📊 Nominator ${index + 1}:`, {
          name: nominator.nominator_name,
          relationship_id: nominator.nomination_relationship_id,
          review_exists: nominator.review_exists
        });
      });
      } else {
        console.warn('⚠️ No nominators found in response');
      }
      
      // Transform API data to match our component interface
      const transformedNominations = nominators.map((nominator: ApiNominator, index: number) => ({
        id: nominator.nomination_relationship_id?.toString() || index.toString(),
        employeeId: nominator.employee_id || `EMP${nominator.nominator_id || index}`,
        employeeName: nominator.nominator_name || 'Unknown',
        designation: nominator.nominator_designation || 'Not specified',
        department: nominator.nominator_department || 'Unknown',
        nominationAs: (nominator.relationship_type === 'peer' ? 'Peer' : 
                      nominator.relationship_type === 'superior' ? 'Superior' : 
                      nominator.relationship_type === 'subordinate' ? 'Subordinate' : 
                      nominator.relationship_type === 'additional_subordinate' ? 'Additional Subordinate' :
                      nominator.relationship_type === 'dotted_superior' ? 'Additional Superior' : 'Stakeholder') as 'Peer' | 'Subordinate' | 'Additional Subordinate' | 'Superior' | 'Stakeholder' | 'Additional Superior',
                      
        nominationRelationshipId: nominator.nomination_relationship_id,
        review_exists: nominator.review_exists || false,
        employeeEmail: nominator.nominator_email || ''
      }));
      
      console.log('� Transformed nominations:', transformedNominations);
      setNominations(transformedNominations);
    } catch (error) {
      console.error('❌ Error fetching nominations:', error);
      
      // Check for 502 Bad Gateway error - navigate to full error page
      if (error && typeof error === 'object' && 'status_code' in error) {
        const statusCode = (error as { status_code: number }).status_code;
        if (statusCode === 502) {
          navigate('/error', {
            state: {
              errorType: 'bad-gateway',
              previousUrl: window.location.pathname,
              retryUrl: window.location.pathname
            }
          });
          return;
        }
      }
      
      // Set empty array on error
      setNominations([]);
    } finally {
      setLoading(false);
    }
  }, [user?.email, authLoading, navigate]);

  useEffect(() => {
    fetchNominations();
  }, [fetchNominations]);

  // Listen for global clearFilters event from SearchFilterBar
  useEffect(() => {
    const handler = () => {
      handleRemoveAllFilters();
    };

    window.addEventListener('clearFilters', handler as EventListener);
    return () => window.removeEventListener('clearFilters', handler as EventListener);
  }, [/* no deps - handler uses stable setters */]);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = () => {
      setOpenMenuId(null);
    };

    if (openMenuId) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [openMenuId]);

  // Filter nominations based on search term and filters with useMemo for performance
  const filteredNominations = useMemo(() => {
    return nominations.filter(nomination => {
      // Text search filter with proper type checking
      const matchesSearch = !searchTerm || (
        (nomination.employeeName || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        String(nomination.employeeId || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (nomination.department || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (nomination.designation || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (nomination.nominationAs || '').toLowerCase().includes(searchTerm.toLowerCase())
      );

      // Dropdown filters
      const matchesDepartment = !filters.department || nomination.department === filters.department;
      const matchesDesignation = !filters.designation || nomination.designation === filters.designation;
      const matchesNominationCapacity = !filters.nominationCapacity || nomination.nominationAs === filters.nominationCapacity;
      
      // Review status filter
      const matchesReviewStatus = !filters.reviewStatus || 
        (filters.reviewStatus === 'Completed' && nomination.review_exists) ||
        (filters.reviewStatus === 'Pending' && !nomination.review_exists);

      return matchesSearch && matchesDepartment && matchesDesignation && matchesNominationCapacity && matchesReviewStatus;
    });
  }, [nominations, searchTerm, filters.department, filters.designation, filters.nominationCapacity, filters.reviewStatus]);

  const totalItems = filteredNominations.length;
  const pageCount = pageSize === 0 ? 1 : Math.ceil(totalItems / pageSize);
  const startIndex = (page - 1) * (pageSize === 0 ? totalItems : pageSize);
  const endIndex = pageSize === 0 ? totalItems : Math.min(startIndex + pageSize, totalItems);
  const currentNominations = pageSize === 0 ? filteredNominations : filteredNominations.slice(startIndex, endIndex);

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  const handleItemsPerPageChange = (value: number) => {
    setPageSize(value);
    setPage(1); // Reset to first page when changing items per page
  };

  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
    setPage(1); // Reset to first page when search changes
  };

  const handleFilterChange = (filterType: string, value: string) => {
    setTempFilters(prev => ({
      ...prev,
      [filterType]: value
    }));
    // Don't reset currentPage here - only when filters are applied
  };

  const handleRemoveAllFilters = () => {
    const emptyFilters = {
      department: '',
      designation: '',
      nominationCapacity: '',
      reviewStatus: ''
    };
    setFilters(emptyFilters);
    setTempFilters(emptyFilters);
    setSearchTerm('');
    setPage(1);
  };

  const handleApplyFilters = () => {
    setFilters(tempFilters); // Apply the temporary filters
    setShowFilters(false);
    setPage(1); // Reset to first page when filters are applied
  };

  const handleOpenFilters = () => {
    setTempFilters(filters); // Initialize temp filters with current filters
    setShowFilters(true);
  };

  const handleCloseFilters = () => {
    setShowFilters(false);
    // Reset temp filters to current applied filters when closing without applying
    setTempFilters(filters);
  };

  // Get unique values for filter dropdowns
  const uniqueDepartments = [...new Set(nominations.map(n => n.department))].sort();
  const uniqueDesignations = [...new Set(nominations.map(n => n.designation))].sort();
  const uniqueNominationCapacities = [...new Set(nominations.map(n => n.nominationAs))].sort();
  
  // Status options dynamically from API data
  const uniqueReviewStatuses = [...new Set(nominations.map(n => n.review_exists ? 'Completed' : 'Pending'))].sort();

  // Check if any filters are active
  const hasActiveFilters = filters.department || filters.designation || filters.nominationCapacity || filters.reviewStatus;

  const handleReviewClick = (nomination: Nomination) => {
    // Navigate to the review tab for this employee
    navigate(`/360-feedback/${nomination.nominationRelationshipId}/review`);
  };

  const handleViewClick = (nomination: Nomination) => {
    navigate(`/360-feedback/${nomination.nominationRelationshipId}/view`);
  };

  const getStatusBadgeClass = (reviewExists: boolean) => {
    if (reviewExists) {
      return 'bg-green-100 text-green-800';
    }
    return 'bg-yellow-100 text-yellow-800';
  };

  const getStatusText = (reviewExists: boolean) => {
    return reviewExists ? 'Completed' : 'Pending';
  };

  // Handle row click - navigate based on review status
  const handleRowClick = (nomination: Nomination) => {
    if (nomination.review_exists) {
      // If review exists, go to view page
      handleViewClick(nomination);
    } else {
      // If review doesn't exist, go to review page
      handleReviewClick(nomination);
    }
  };

  if (!hasActiveCycle) {
    return <NoActiveCycle />;
  }

  if (loading) {
    return (
      <div className="min-h-screen flex justify-center items-center">
        <div className="text-center">
          <LoadingSpinner />
          <p className="mt-2 text-sm text-gray-600">
            {!user ? 'Loading user data...' : !user.email ? 'No user email found' : 'Loading nominations...'}
          </p>
          {user && (
            <p className="mt-1 text-xs text-gray-500">
              User: {user.email || 'No email'}
            </p>
          )}
        </div>
      </div>
    );
  }

  // If no user data available
  if (!user) {
    return (
      <ErrorState
        type="not-found"
        title="Authentication Required"
        message="No user data available. Please log in to continue."
        showRetryButton={false}
      />
    );
  }

  return (
    <div className="space-y-6">
      {/* Custom styles for this page only */}
      <style>{`
        *:focus-visible {
          outline: none !important;
          outline-offset: 0 !important;
        }
        
      `}</style>

      {/* Page Header */}
     <div className='' style={{ marginTop: '0px' }}>

      {/* Search and Filter Section (reusable) */}
      <SearchFilterBar
        searchTerm={searchTerm}
        onSearchChange={handleSearchChange}
        onClearSearch={() => handleSearchChange('')}
        onOpenFilters={handleOpenFilters}
        hasActiveFilters={!!hasActiveFilters}
        activeFiltersCount={[filters.department, filters.designation, filters.nominationCapacity, filters.reviewStatus].filter(Boolean).length}
        tuneIcon={tuneIcon}
      />
      </div>

      <FiltersModal
        show={showFilters}
        tempFilters={tempFilters}
        uniqueDepartments={uniqueDepartments}
        uniqueDesignations={uniqueDesignations}
        uniqueNominationCapacities={uniqueNominationCapacities}
        uniqueReviewStatuses={uniqueReviewStatuses}
        onChange={handleFilterChange}
        onApply={handleApplyFilters}
        onRemoveAll={handleRemoveAllFilters}
        onClose={handleCloseFilters}
        arrowDropDownIcon={arrowDropDownIcon}
        isNominationReviewPage={true}
      />

      {/* Table Card */}
      
      <Card>
        <div className="table-container">
          {/* Table Header */}
         

          <div className="bg-primary-800 rounded-lg px-6 py-3 border-b border-gray-200 mb-2">
            <div className="grid grid-cols-12 gap-4 text-left text-base font-medium text-white  tracking-wider">
              <div className="col-span-1">SNO.</div>
              <div className="col-span-2">Employee ID</div>
              <div className="col-span-2">Employee Name</div>
              <div className="col-span-2">Nomination as</div>
              <div className="col-span-2 px-2.5">Department</div>
              <div className="col-span-2 px-2.5">Designation</div>
              {/* <div className="col-span-1 px-6">Action</div> */}
              <div className="col-span-1 px-6">Status</div>
            </div>
          </div>

          {/* Table Body */}
          <div className="space-y-3 relative pb-4">
            {currentNominations.length === 0 ? (
              <div className="text-center py-12">
                <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-gray-100 mb-4">
                  <svg className="h-6 w-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  {nominations.length === 0 ? 'No Nominations Found' : 'No Results Found'}
                </h3>
                <p className="text-gray-500 mb-4">
                  {nominations.length === 0 
                    ? 'You don\'t have any pending nomination reviews at the moment.' 
                    : hasActiveFilters 
                      ? 'No nominations match your current search criteria. Try adjusting your filters or search term.'
                      : 'No nominations found.'
                  }
                </p>
                {hasActiveFilters && (
                  <button
                    onClick={handleRemoveAllFilters}
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-purple-600 bg-purple-100 hover:bg-purple-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500"
                  >
                    Clear all filters
                  </button>
                )}
              </div>
            ) : (
              currentNominations.map((nomination, index) => (
                <div
                  key={nomination.id}
                  onClick={() => handleRowClick(nomination)}
                  tabIndex={0}
                  onKeyDown={(e) => { if (e.key === 'Enter') handleRowClick(nomination); }}
                  className="py-2 group hover:bg-gray-50 border border-gray-200 rounded-lg pl-4 transition-colors mb-3 relative focus-within:ring-2 focus-within:ring-inset focus-within:ring-primary-300 focus-within:outline-none cursor-pointer"
                >
                  <div className="grid grid-cols-12 gap-4 items-center">
                    <div className="px-6 col-span-1 text-base text-gray-600">
                      <span className="bg-transparent rounded">{startIndex + index + 1}.</span>
                    </div>
                  <div className="col-span-2">
                    <div className="text-base ml-1 text-gray-600">{nomination.employeeId}</div>
                  </div>
                  <div className="col-span-2">
                    <div className="flex items-center">
                      <NameWithEmailTooltip
                        as="div"
                        className="text-base text-gray-600"
                        name={nomination.employeeName}
                        email={nomination.employeeEmail}
                      />
                    </div>
                  </div>
                  <div className="col-span-2">
                    <span className="inline-flex items-center  py-0.5 rounded-full text-base  text-gray-600">
                      {nomination.nominationAs}
                    </span>
                  </div>
                  <div className="col-span-2">
                    <div className="text-base text-gray-600">{nomination.department}</div>
                  </div>
                  <div className="col-span-2">
                    <div className="text-base text-gray-600">{nomination.designation}</div>
                  </div>


                  <div className="col-span-1">
                    <span className={`inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium ${getStatusBadgeClass(nomination.review_exists || false)}`}>
                      {getStatusText(nomination.review_exists || false)}
                    </span>
                  </div>


                   {/* <div className="col-span-2">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusBadgeClass(review.reviewStatus)}`}>
                        {review.reviewStatus}
                      </span>
                    </div> */}


                  {/* <div className="col-span-1 text-base ">
                    <div className="flex items-center justify-center">
                     
                      <div className="relative">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleMenu(nomination.id, e);
                          }}
                          className="p-2 text-black hover:text-gray-600 focus:outline-none focus:text-gray-600 transition-colors rounded-full hover:bg-gray-100"
                        >
                          <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                            <path d="M10 6a2 2 0 110-4 2 2 0 010 4zM10 12a2 2 0 110-4 2 2 0 010 4zM10 18a2 2 0 110-4 2 2 0 010 4z" />
                          </svg>
                        </button>
                        

                        {openMenuId === nomination.id && (
                          <div 
                            className={`absolute right-0 w-48 bg-white border border-gray-200 rounded-lg shadow-lg z-[9999] ${
                              menuPosition === 'top' ? 'bottom-full mb-2' : 'top-full mt-2'
                            }`}
                            onClick={(e) => e.stopPropagation()}
                          >
                            <div className="py-1">
                              {nomination.review_exists ? (

                                <button
                                  onClick={() => handleMenuAction('view', nomination)}
                                  className="flex items-center w-full px-4 py-2 text-base text-black hover:bg-gray-100"
                                >
                                  <svg className="mr-3 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                  </svg>
                                  View Details
                                </button>
                              ) : (

                                <button
                                  onClick={() => handleMenuAction('review', nomination)}
                                  className="flex items-center w-full px-4 py-2 text-base text-black hover:bg-gray-100"
                                >
                                  <svg className="mr-3 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                  </svg>
                                  Review
                                </button>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div> */}
                  

                </div>
              </div>
              ))
            )}
          </div>
        </div>

        {/* Pagination - Only show when there are results */}
        {pageSize > 0 && totalItems > 0 && (
          <div className="px-4 py-3 border-t border-gray-200">
            <Pagination
              page={page}
              pageCount={pageCount}
              onPageChange={handlePageChange}
              pageSize={pageSize}
              onPageSizeChange={handleItemsPerPageChange}
              pageSizeOptions={PageSizeOptions}
            />
          </div>
        )}
      </Card>
    </div>
  );
};

export default NominationReviewPageView;
